<template>
  <div class="tabela-osd">
    <div v-if="$slots['toolbar-actions'] || $slots['toolbar-filters']" class="table-toolbar">
      <div class="toolbar-head">
        <div v-if="$slots['toolbar-actions']" class="toolbar-actions">
          <slot name="toolbar-actions" />
        </div>
        <div v-if="$slots['toolbar-filters']" class="toolbar-filters">
          <slot name="toolbar-filters" />
        </div>
      </div>
    </div>

    <div class="table-responsive table-content">
      <div v-if="carregando" class="loading-overlay" role="status">
        <div class="spinner-border" />
      </div>

      <table class="table table-hover table-sm align-middle mb-0 native-table">
        <thead>
          <tr>
            <th class="line-number">
              #
            </th>
            <th v-for="coluna in colunas" :key="coluna.field" :class="coluna.tdClass">
              <button
                class="sort-header"
                type="button"
                :disabled="!colunaEhOrdenavel(coluna)"
                @click="aoOrdenarColuna(coluna)"
              >
                <span>{{ obterLabelColuna(coluna) }}</span>
                <i v-if="colunaEhOrdenavel(coluna)" class="bi" :class="iconeOrdenacao(coluna.field)" />
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!carregando && !linhas.length">
            <td :colspan="colunas.length + 1">
              <div class="empty-state">
                <span>{{ textoTabelaVazia }}</span>
                <button
                  v-if="mostrarBotaoRecarregar"
                  type="button"
                  class="btn btn-sm btn-secondary"
                  :disabled="carregando"
                  @click="$emit('reload')"
                >
                  <i class="bi bi-arrow-clockwise" />
                </button>
              </div>
            </td>
          </tr>
          <tr
            v-for="(linha, index) in linhas"
            :key="obterChaveDaLinha(linha, index)"
            @click="aoClicarLinha(linha, $event)"
          >
            <td class="line-number">
              {{ obterNumeroDaLinha(index) }}
            </td>
            <td
              v-for="coluna in colunas"
              :key="`${obterChaveDaLinha(linha, index)}-${coluna.field}`"
              :class="coluna.tdClass"
            >
              <slot
                v-if="$slots[`cell-${coluna.field}`]"
                :name="`cell-${coluna.field}`"
                :linha="linha"
                :coluna="coluna"
                :valor="linha[coluna.field]"
                :index="index"
              />
              <template v-else>
                {{ formatarCelula(linha, coluna) }}
              </template>
            </td>
          </tr>
        </tbody>
      </table>

      <TooltipModal
        :visivel="tooltipModal.visivel"
        :campos="camposModalCalculados"
        :posicao="tooltipModal.posicao"
        @fechar="fecharTooltipModal"
      />
    </div>

    <div v-if="totalLinhas > 0" class="table-footer">
      <div class="footer-info">
        {{ textoTotal }}
      </div>
      <VPagination
        v-model="paginaAtual"
        :records="totalLinhas"
        :per-page="itensPorPagina"
        :options="opcoesPaginacaoResolvidas"
        @paginate="aoPaginar"
      />
    </div>
  </div>
</template>

<script>
import VPagination from 'v-pagination-3';
import TooltipModal from './tooltipModal.vue';

export default {
  components: { VPagination, TooltipModal },
  props: {
    colunas: { type: Array, required: true },
    linhas: { type: Array, required: true },
    carregando: { type: Boolean, required: true },
    totalLinhas: { type: Number, required: true },
    pagina: { type: Number, required: true },
    campoOrdenacao: { type: String, default: '' },
    ordemOrdenacao: { type: String, default: 'desc' },
    textoTabelaVazia: { type: String, default: '' },
    chaveLinha: { type: String, default: 'id' },
    mostrarBotaoRecarregar: { type: Boolean, default: true },
    camposModal: { type: Function, default: null },
    configPaginacao: {
      type: Object,
      default: () => ({
        chunk: 5,
        chunksNavigation: 'scroll',
        edgeNavigation: true,
        texts: {
          count: '{from} - {to} de {count}',
          first: '|<',
          last: '>|',
        },
      }),
    },
  },
  emits: ['sort-change', 'page-change', 'reload'],
  data: () => ({
    paginaAtual: 1,
    itensPorPagina: 10,
    linhaSelecionada: null,
    tooltipModal: {
      visivel: false,
      posicao: { x: 0, y: 0 },
    },
  }),
  computed: {
    textoTotal() {
      const primeiro = ((this.paginaAtual - 1) * this.itensPorPagina) + 1;
      const ultimo = Math.min(this.paginaAtual * this.itensPorPagina, this.totalLinhas);
      return `${primeiro} - ${ultimo} de ${this.totalLinhas}`;
    },
    opcoesPaginacaoResolvidas() {
      return {
        chunk: 5,
        chunksNavigation: 'scroll',
        edgeNavigation: true,
        texts: { ...this.configPaginacao?.texts, count: '' },
      };
    },
    camposModalCalculados() {
      if (!this.camposModal || !this.linhaSelecionada) return [];
      return this.camposModal(this.linhaSelecionada);
    },
  },
  watch: {
    pagina: {
      immediate: true,
      handler(valor) {
        this.paginaAtual = valor;
      },
    },
  },
  methods: {
    colunaEhOrdenavel(coluna) {
      return coluna.sortable !== false;
    },
    aoOrdenarColuna(coluna) {
      if (!this.colunaEhOrdenavel(coluna)) return;
      this.$emit('sort-change', coluna.field);
    },
    aoPaginar() {
      this.fecharTooltipModal();
      this.$emit('page-change', this.paginaAtual);
    },
    aoClicarLinha(linha, evento) {
      if (!this.camposModal) return;
      this.linhaSelecionada = linha;
      this.tooltipModal.posicao = {
        x: Math.min(evento.clientX + 12, window.innerWidth - 220),
        y: Math.min(evento.clientY + 12, window.innerHeight - 100),
      };
      this.tooltipModal.visivel = true;
    },
    fecharTooltipModal() {
      this.tooltipModal.visivel = false;
    },
    obterNumeroDaLinha(index) {
      return ((this.paginaAtual - 1) * this.itensPorPagina) + index + 1;
    },
    obterChaveDaLinha(linha, index) {
      return linha[this.chaveLinha] ?? index;
    },
    iconeOrdenacao(campo) {
      if (this.campoOrdenacao !== campo) return 'bi-chevron-expand';
      return this.ordemOrdenacao === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down';
    },
    obterLabelColuna(coluna) {
      if (coluna.label) return coluna.label;
      if (!coluna.labelKey) return '';
      const quantidadePlural = coluna.pluralQuantidade ?? 2;
      return coluna.plural ? this.$t(coluna.labelKey, quantidadePlural) : this.$t(coluna.labelKey);
    },
    formatarCelula(linha, coluna) {
      const valor = linha[coluna.field];
      if (valor == null || valor === '') return '-';
      if (coluna.formatFn) return coluna.formatFn(valor);
      return valor;
    },
  },
};
</script>
